/**
 * DeBank API Service
 * Premium blockchain data provider with comprehensive DeFi tracking
 *
 * API Tiers:
 * - Free: Limited requests, basic endpoints
 * - Pro: Unlimited requests, all endpoints, real-time data
 *
 * If VITE_DEBANK_API_KEY is set, uses DeBank Pro exclusively
 * Otherwise, falls back to free tier with other APIs
 */

import axios from 'axios';
import type {
  Chain,
  UnifiedToken,
  DeFiPosition,
  NFTItem,
  DeBankConfig,
  AggregatedWalletData
} from '../types/wallet.types';

// DeBank API Configuration
const DEBANK_API_KEY = import.meta.env.VITE_DEBANK_API_KEY;
const isDev = import.meta.env.DEV;

// In development: Use Vite proxy (direct API calls)
// In production: Use Vercel serverless proxy (API key on server)
const USE_SERVERLESS_PROXY = !isDev;
const DEBANK_PRO_BASE_URL = isDev ? '/debank-pro-api' : '';
const DEBANK_FREE_BASE_URL = isDev ? '/debank-free-api' : '';

// In production, DeBank is available via serverless proxy (key on server)
// In development, check for VITE_DEBANK_API_KEY
const IS_DEBANK_AVAILABLE = USE_SERVERLESS_PROXY || !!DEBANK_API_KEY;

// Chain ID mapping for DeBank
const DEBANK_CHAIN_IDS: Record<Chain, string> = {
  ethereum: 'eth',
  bsc: 'bsc',
  polygon: 'matic',
  arbitrum: 'arb',
  optimism: 'op',
  avalanche: 'avax',
  base: 'base',
  fantom: 'ftm',
  gnosis: 'xdai'
};

// DeBank API response types
interface DeBankTokenResponse {
  id: string;
  chain: string;
  name: string;
  symbol: string;
  display_symbol?: string;
  decimals: number;
  logo_url?: string;
  price: number;
  is_verified?: boolean;
  amount: number;
  raw_amount: number;
}

interface DeBankProtocolResponse {
  id: string;
  chain: string;
  name: string;
  site_url?: string;
  logo_url?: string;
  portfolio_item_list: DeBankPortfolioItem[];
}

interface DeBankPortfolioItem {
  name: string;
  detail_types: string[];
  detail: {
    supply_token_list?: DeBankTokenResponse[];
    borrow_token_list?: DeBankTokenResponse[];
    reward_token_list?: DeBankTokenResponse[];
    health_rate?: number;
  };
  stats: {
    asset_usd_value: number;
    debt_usd_value: number;
    net_usd_value: number;
  };
}

interface DeBankNFTResponse {
  id: string;
  contract_id: string;
  inner_id: string;
  chain: string;
  name?: string;
  description?: string;
  content_type?: string;
  content?: string;
  thumbnail_url?: string;
  collection?: {
    id: string;
    name: string;
    floor_price?: number;
  };
}

/**
 * Get DeBank configuration
 */
export function getDeBankConfig(): DeBankConfig {
  const isProEnabled = !!DEBANK_API_KEY;
  return {
    isProEnabled,
    apiKey: DEBANK_API_KEY,
    baseUrl: isProEnabled ? DEBANK_PRO_BASE_URL : DEBANK_FREE_BASE_URL
  };
}

/**
 * Check if DeBank Pro is available
 * In production: Always true (uses serverless proxy with API key on server)
 * In development: Requires VITE_DEBANK_API_KEY
 */
export function isDeBankProEnabled(): boolean {
  return IS_DEBANK_AVAILABLE;
}

/**
 * Create axios instance with proper headers (for development/Vite proxy)
 */
function createDeBankClient() {
  const config = getDeBankConfig();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (config.isProEnabled && config.apiKey) {
    headers['AccessKey'] = config.apiKey;
  }

  return axios.create({
    baseURL: config.baseUrl,
    headers,
    timeout: 30000
  });
}

/**
 * Call DeBank API via Vercel serverless proxy (for production)
 */
async function callDeBankProxy(endpoint: string, params: Record<string, unknown> = {}): Promise<any> {
  const response = await axios.post('/api/debank/proxy', {
    endpoint,
    params
  }, {
    timeout: 30000
  });
  return response.data;
}

/**
 * Convert DeBank chain ID to our Chain type
 */
function debankChainToChain(debankChain: string): Chain {
  const mapping: Record<string, Chain> = {
    eth: 'ethereum',
    bsc: 'bsc',
    matic: 'polygon',
    arb: 'arbitrum',
    op: 'optimism',
    avax: 'avalanche',
    base: 'base',
    ftm: 'fantom',
    xdai: 'gnosis'
  };
  return mapping[debankChain] || 'ethereum';
}

/**
 * Convert DeBank token to UnifiedToken
 */
function convertDeBankToken(token: DeBankTokenResponse): UnifiedToken {
  return {
    symbol: token.display_symbol || token.symbol,
    name: token.name,
    address: token.id,
    chain: debankChainToChain(token.chain),
    balance: token.amount,
    balanceUSD: token.amount * token.price,
    price: token.price,
    logoUrl: token.logo_url,
    isVerified: token.is_verified,
    source: ['debank']
  };
}

/**
 * Get total balance for a wallet (all chains)
 */
export async function getTotalBalance(address: string): Promise<number> {
  try {
    if (USE_SERVERLESS_PROXY) {
      const data = await callDeBankProxy('/v1/user/total_balance', { id: address.toLowerCase() });
      return data.total_usd_value || 0;
    }

    const client = createDeBankClient();
    const response = await client.get('/v1/user/total_balance', {
      params: { id: address.toLowerCase() }
    });
    return response.data.total_usd_value || 0;
  } catch (error) {
    console.error('❌ DeBank getTotalBalance error:', error);
    throw error;
  }
}

/**
 * Get all token balances for a wallet (all chains)
 */
export async function getAllTokenBalances(address: string): Promise<UnifiedToken[]> {
  try {
    let data: DeBankTokenResponse[];

    if (USE_SERVERLESS_PROXY) {
      data = await callDeBankProxy('/v1/user/all_token_list', {
        id: address.toLowerCase(),
        is_all: true
      });
    } else {
      const client = createDeBankClient();
      const response = await client.get('/v1/user/all_token_list', {
        params: {
          id: address.toLowerCase(),
          is_all: true
        }
      });
      data = response.data || [];
    }

    const tokens: DeBankTokenResponse[] = data || [];
    return tokens
      .filter(t => t.amount > 0)
      .map(convertDeBankToken)
      .sort((a, b) => b.balanceUSD - a.balanceUSD);
  } catch (error) {
    console.error('❌ DeBank getAllTokenBalances error:', error);
    throw error;
  }
}

/**
 * Get token balances for a specific chain
 */
export async function getTokenBalancesByChain(
  address: string,
  chain: Chain
): Promise<UnifiedToken[]> {
  const chainId = DEBANK_CHAIN_IDS[chain];

  try {
    let data: DeBankTokenResponse[];

    if (USE_SERVERLESS_PROXY) {
      data = await callDeBankProxy('/v1/user/token_list', {
        id: address.toLowerCase(),
        chain_id: chainId,
        is_all: true
      });
    } else {
      const client = createDeBankClient();
      const response = await client.get('/v1/user/token_list', {
        params: {
          id: address.toLowerCase(),
          chain_id: chainId,
          is_all: true
        }
      });
      data = response.data || [];
    }

    const tokens: DeBankTokenResponse[] = data || [];
    return tokens
      .filter(t => t.amount > 0)
      .map(convertDeBankToken)
      .sort((a, b) => b.balanceUSD - a.balanceUSD);
  } catch (error) {
    console.error(`❌ DeBank getTokenBalancesByChain error for ${chain}:`, error);
    throw error;
  }
}

/**
 * Get all DeFi protocol positions
 */
export async function getAllDeFiPositions(address: string): Promise<DeFiPosition[]> {
  const config = getDeBankConfig();

  console.log(`📡 DeBank DeFi: Fetching positions for ${address.slice(0, 10)}... (Pro: ${config.isProEnabled}, Serverless: ${USE_SERVERLESS_PROXY})`);

  try {
    let data: DeBankProtocolResponse[];

    if (USE_SERVERLESS_PROXY) {
      const rawData = await callDeBankProxy('/v1/user/all_complex_protocol_list', { id: address.toLowerCase() });
      console.log(`📡 DeBank DeFi raw response:`, JSON.stringify(rawData).slice(0, 500));
      // Handle both array response and object with data property
      data = Array.isArray(rawData) ? rawData : (rawData?.data || rawData || []);
      console.log(`📡 DeBank DeFi response data type: ${typeof data}, isArray: ${Array.isArray(data)}, length: ${Array.isArray(data) ? data.length : 'N/A'}`);
    } else {
      const client = createDeBankClient();
      const response = await client.get('/v1/user/all_complex_protocol_list', {
        params: { id: address.toLowerCase() }
      });
      console.log(`📡 DeBank DeFi response status: ${response.status}`);
      console.log(`📡 DeBank DeFi response data type: ${typeof response.data}, isArray: ${Array.isArray(response.data)}`);
      data = response.data;
    }

    if (!data || !Array.isArray(data)) {
      console.warn('⚠️ DeBank DeFi: Invalid response data - expected array, got:', typeof data);
      return [];
    }

    const protocols: DeBankProtocolResponse[] = data;
    console.log(`📡 DeBank DeFi: Found ${protocols.length} protocols`);

    const positions: DeFiPosition[] = [];

    for (const protocol of protocols) {
      console.log(`📡 DeBank DeFi: Processing protocol ${protocol.name} (${protocol.id}) with ${protocol.portfolio_item_list?.length || 0} items`);

      if (!protocol.portfolio_item_list) continue;

      for (const item of protocol.portfolio_item_list) {
        // Determine position type
        let positionType: DeFiPosition['type'] = 'other';
        const detailTypes = item.detail_types || [];

        if (detailTypes.includes('lending') || detailTypes.includes('Lending')) positionType = 'lending';
        else if (detailTypes.includes('staked') || detailTypes.includes('Staked')) positionType = 'staking';
        else if (detailTypes.includes('farming') || detailTypes.includes('Farming') || detailTypes.includes('Yield')) positionType = 'farming';
        else if (detailTypes.includes('liquidity') || detailTypes.includes('Liquidity')) positionType = 'liquidity';
        else if (detailTypes.includes('vesting') || detailTypes.includes('Vesting') || detailTypes.includes('Locked')) positionType = 'vesting';
        else if (detailTypes.includes('reward') || detailTypes.includes('Reward')) positionType = 'farming';

        const netValue = item.stats?.net_usd_value || 0;

        positions.push({
          protocolId: protocol.id,
          protocolName: protocol.name,
          protocolLogo: protocol.logo_url,
          chain: debankChainToChain(protocol.chain),
          type: positionType,
          suppliedTokens: (item.detail?.supply_token_list || []).map(convertDeBankToken),
          borrowedTokens: (item.detail?.borrow_token_list || []).map(convertDeBankToken),
          rewardTokens: (item.detail?.reward_token_list || []).map(convertDeBankToken),
          netValueUSD: netValue,
          healthFactor: item.detail?.health_rate,
          source: 'debank'
        });
      }
    }

    console.log(`✅ DeBank DeFi: Total ${positions.length} positions found`);
    return positions.sort((a, b) => b.netValueUSD - a.netValueUSD);
  } catch (error: any) {
    console.error('❌ DeBank getAllDeFiPositions error:', error?.message || error);
    if (error?.response) {
      console.error('❌ DeBank DeFi response status:', error.response.status);
      console.error('❌ DeBank DeFi response data:', JSON.stringify(error.response.data));
    }
    // Return empty array instead of throwing to not break the whole fetch
    return [];
  }
}

/**
 * Get NFT list for a wallet
 */
export async function getNFTList(address: string): Promise<NFTItem[]> {
  try {
    let data: DeBankNFTResponse[];

    if (USE_SERVERLESS_PROXY) {
      data = await callDeBankProxy('/v1/user/all_nft_list', {
        id: address.toLowerCase(),
        is_all: true
      });
    } else {
      const client = createDeBankClient();
      const response = await client.get('/v1/user/all_nft_list', {
        params: {
          id: address.toLowerCase(),
          is_all: true
        }
      });
      data = response.data || [];
    }

    const nfts: DeBankNFTResponse[] = data || [];
    return nfts.map(nft => ({
      contractAddress: nft.contract_id,
      tokenId: nft.inner_id,
      name: nft.name || `#${nft.inner_id}`,
      description: nft.description,
      imageUrl: nft.thumbnail_url || nft.content,
      chain: debankChainToChain(nft.chain),
      collection: {
        name: nft.collection?.name || 'Unknown Collection',
        floorPrice: nft.collection?.floor_price
      },
      source: 'debank'
    }));
  } catch (error) {
    console.error('❌ DeBank getNFTList error:', error);
    throw error;
  }
}

/**
 * Get complete wallet data from DeBank
 * This is the main function to use when DeBank Pro is enabled
 */
export async function getCompleteWalletData(address: string): Promise<AggregatedWalletData> {
  const config = getDeBankConfig();

  console.log(`📡 DeBank: Fetching complete wallet data for ${address.slice(0, 10)}...`);
  console.log(`📡 DeBank: Pro enabled: ${config.isProEnabled}, Base URL: ${config.baseUrl}`);

  if (!config.isProEnabled) {
    console.warn('⚠️ DeBank Pro not enabled. Using limited free tier.');
  }

  try {
    // Fetch all data in parallel
    console.log('📡 DeBank: Starting parallel fetch...');

    const results = await Promise.allSettled([
      getTotalBalance(address),
      getAllTokenBalances(address),
      getAllDeFiPositions(address),
      getNFTList(address)
    ]);

    // Extract results with fallbacks
    const totalBalance = results[0].status === 'fulfilled' ? results[0].value : 0;
    const tokens = results[1].status === 'fulfilled' ? results[1].value : [];
    const defiPositions = results[2].status === 'fulfilled' ? results[2].value : [];
    const nfts = results[3].status === 'fulfilled' ? results[3].value : [];

    // Log any failures
    results.forEach((result, index) => {
      const names = ['totalBalance', 'tokens', 'defiPositions', 'nfts'];
      if (result.status === 'rejected') {
        console.error(`❌ DeBank ${names[index]} failed:`, result.reason);
      }
    });

    console.log(`✅ DeBank: Fetched ${tokens.length} tokens, ${defiPositions.length} DeFi positions, ${nfts.length} NFTs`);

    // Determine chains used
    const chainsUsed = [...new Set([
      ...tokens.map(t => t.chain),
      ...defiPositions.map(p => p.chain),
      ...nfts.map(n => n.chain)
    ])] as Chain[];

    return {
      address: address.toLowerCase(),
      totalValueUSD: totalBalance,
      tokens,
      defiPositions,
      nfts,
      chainsUsed,
      lastUpdated: new Date().toISOString(),
      sources: ['debank'],
      confidence: 95 // DeBank Pro has high data quality
    };
  } catch (error) {
    console.error('❌ DeBank getCompleteWalletData error:', error);
    throw error;
  }
}

// Export config checker for use in aggregator
export const debankApi = {
  isDeBankProEnabled,
  getDeBankConfig,
  getTotalBalance,
  getAllTokenBalances,
  getTokenBalancesByChain,
  getAllDeFiPositions,
  getNFTList,
  getCompleteWalletData
};

export default debankApi;
